import { useEffect, useState } from 'react'

import logger from '@/utils/logger'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'

import { useToaster } from '@/components/Toast/useToaster'

import type { WebPushNotificationsInterface } from '../../constants'
import getWebPushSubscription from './getWebPushSubscription'
import subscribeWebPush from './subscribeWebPush'
import unsubscribeWebPush from './unsubscribeWebPush'

export default function usePushNotifications(): WebPushNotificationsInterface {
  const toast = useToaster()
  const [browserSupportsWebPush, setBrowserSupportsWebPush] = useState(false)
  const [existingSubscription, setExistingSubscription] = useState<PushSubscription | null>(null)

  useEffect(() => {
    const notificationApiAvailable = 'Notification' in globalThis
    setBrowserSupportsWebPush(notificationApiAvailable)

    if (!notificationApiAvailable) {
      return
    }

    getWebPushSubscription()
      .then(subscription => {
        setExistingSubscription(subscription)
      })
      .catch(err => {
        logger.error('Error getting web push subscription', { err })
      })
  }, [])

  const [toggleSubscription] = useAPIWrapper(
    async () => {
      if (!browserSupportsWebPush) {
        return
      }

      if (existingSubscription == null) {
        if (Notification.permission !== 'granted') {
          // If the user has explicitly denied the permission, this won't re-request it.
          // Instead, it'll error out and we'll show a toast telling them to enable it.
          await Notification.requestPermission()
        }
        try {
          const subscription = await subscribeWebPush()
          setExistingSubscription(subscription)
        } catch (err) {
          if (err?.name === 'NotAllowedError') {
            throw new Error('You must first enable notifications in your browser settings')
          }
          throw err
        }
      } else {
        await unsubscribeWebPush(existingSubscription)
        setExistingSubscription(null)
      }
      toast({
        title: `Notifications successfully ${
          existingSubscription == null ? 'enabled' : 'disabled'
        }`,
        status: 'success',
      })
    },
    [browserSupportsWebPush, existingSubscription, toast],
    'Toggle WebPush Notifications'
  )

  return {
    isSupported: browserSupportsWebPush,
    isEnabled: existingSubscription != null,
    toggleSubscription,
    element: null,
  }
}
